import { Box, Flex, Heading, Image } from '@chakra-ui/react'
import { ReactNode } from 'react'

import { useDocumentTitle } from '../../helpers'

// Logo "ABRA | Academia Brasileira de Arte" shown on the right of the banner.
const ABRA_LOGO_URL = '/img/logo.svg'

// Card/banner content max width. Standardized across all public forms.
const MAX_WIDTH = '900px'

interface IPublicFormLayoutProps {
  /** Form name shown on the left of the banner. */
  title: ReactNode
  /** Form content rendered inside the white card. */
  children: ReactNode
  /** Tab title, for when `title` is a `ReactNode`. A plain string already stands on its own. */
  documentTitle?: string
}

export default function PublicFormLayout({ title, children, documentTitle }: IPublicFormLayoutProps) {
  // Same reason as `PageTitle`: whatever renders the `h1` renders the tab title.
  useDocumentTitle(documentTitle ?? (typeof title === 'string' ? title : undefined))

  return (
    <Box minH="100vh" pb={{ base: 8, md: 14 }} overflowX="hidden">
      {/* Banner. Full-bleed: cancels the gutters of DashboardPage's .dashboard-content
          wrapper so the gradient reaches both edges of the screen. */}
      <Box
        position="relative"
        overflow="hidden"
        ml="calc(-1 * var(--dashboard-gutter-left, 0px))"
        mr="calc(-1 * var(--dashboard-gutter-right, 0px))"
        px={{ base: 5, md: 8 }}
        pt={{ base: 10, md: 24 }}
        pb={{ base: 14, md: 36 }}
        bgGradient="linear(100deg, #f5005e -10%, #cf006c 15%, #9c2a8e 40%, #7a2b9e 70%, #6d2ca1 115%)"
      >
        {/* Corner accent, top-left */}
        <Image
          aria-hidden
          src="/img/form-header-accent-left.svg"
          alt=""
          position="absolute"
          top={{ base: '-20px', md: '-65px' }}
          left={{ base: '-20px', md: '-65px' }}
          w={{ base: '110px', md: '320px' }}
          h="auto"
          pointerEvents="none"
        />
        {/* Accent shape fading in toward the right */}
        <Image
          aria-hidden
          src="/img/form-header-accent-right.svg"
          alt=""
          position="absolute"
          top={0}
          right={0}
          h="100%"
          w="auto"
          maxW={{ base: '30%', md: '50%' }}
          objectFit="cover"
          objectPosition="right"
          pointerEvents="none"
        />

        <Flex
          position="relative"
          maxW={MAX_WIDTH}
          mx="auto"
          align="center"
          justify="space-between"
          gap={{ base: 4, md: 8 }}
        >
          <Heading
            as="h1"
            color="white"
            fontWeight={500}
            lineHeight={1.25}
            fontSize={{ base: '1.5rem', md: '2.4rem' }}
            maxW="50%"
            minW={0}
            overflowWrap="anywhere"
          >
            {title}
          </Heading>

          <Image
            src={ABRA_LOGO_URL}
            alt="ABRA - Academia Brasileira de Arte"
            w={{ base: '110px', md: '190px' }}
            h={{ base: '26px', md: '46px' }}
            objectFit="contain"
            objectPosition="right"
            flexShrink={0}
          />
        </Flex>
      </Box>

      {/* White card, overlapping the banner */}
      <Box maxW={MAX_WIDTH} mx="auto" px={{ base: 3, md: 4 }} mt={{ base: -12, md: -16 }} position="relative">
        <Box
          bg="white"
          border="1px solid"
          borderColor="gray.400"
          borderRadius={{ base: 'xl', md: '2xl' }}
          boxShadow="xl"
          p={{ base: 5, md: 10 }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  )
}
